
import { Head, usePage } from '@inertiajs/react'
import { TypeSeo } from '@/types/ctf'
import Routes from '@/constants/routes'
import { getAltPath } from '@/utils/strings.ts'

type MetaProps = {
  seo: TypeSeo<'WITHOUT_UNRESOLVABLE_LINKS'>
  breadcrumb?: { name: string; path: string }[]
  hasMd: boolean
}

export default function Meta({ seo, breadcrumb = [] }: MetaProps, hasMd = false) {
  const { url } = usePage()

  let fullTitle = seo?.fields?.title || ''
  if (fullTitle) {
    fullTitle += ' | '
  }
  fullTitle += 'Bitwarden'
  // escape quotes in title
  fullTitle = fullTitle.replace(/"/g, '&quot;').replace(/'/g, '&#39;')

  const imageUrl = seo?.fields?.ogImage?.fields?.file?.url || 'https://bitwarden.com/assets/7MxCY8VttLPQLbByiotlug/912d4b50c0ddccbb93478c0d1fad3fe8/bitwarden.com.png'

  // todo: not working as is.  need to change this field to use `Component: Link`
  let canonicalPath = `${seo?.fields?.canonical?.entry?.fields?.path || Routes.hostname + url}`
  if (canonicalPath.includes('?')) {
    const url = new URL(canonicalPath)
    canonicalPath = `${url.origin}${url.pathname}`
  }

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={seo?.fields?.description || ''} />
      <meta name="keywords" content={seo?.fields?.keywords?.join(', ') || ''} />

      {seo?.fields?.noIndex && <meta name="robots" content="noindex" />}
      {seo?.fields?.noFollow && <meta name="robots" content="nofollow" />}

      <link rel="canonical" href={canonicalPath} />
      {hasMd && <link rel="alternate" type="text/markdown" href={Routes.hostname + getAltPath(url, '.md')} />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={seo?.fields?.description || ''} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={canonicalPath} />

      <meta property="twitter:site" content="@bitwarden" />
      <meta property="twitter:domain" content="bitwarden.com" />
      <meta property="twitter:url" content={imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={seo?.fields?.description} />
      <meta name="twitter:image" content={imageUrl} />
      {breadcrumb?.length > 1 && (
        <script type="application/ld+json">
          {`{
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type" : "ListItem",
                  "position": "1",
                  "name": "${breadcrumb[0].name}",
                  "item": "${Routes.hostname}${breadcrumb[0].path}"
                },
                {
                  "@type": "ListItem",
                  "position": "2",
                  "name": "${breadcrumb[1].name}"
                }
              ]
            }`}
        </script>
      )}
    </Head>
  )
}
